import React, { useEffect, useRef, useState } from "react";
import { Box, Heading, Button, VStack, Text, useToast, Flex } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Plot from 'react-plotly.js';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { getStatisticChartsADP, initSurveyService } from "../../services/http/surveyHttpService";
import createAxiosInstance from "../../interceptors/useAxiosInterceptors";
import logout from "../../services/authService";
import { useSession } from "../../SessionContext";
import { MdArrowBack, } from "react-icons/md";

const ChartResponseView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [charts, setCharts] = useState([]);
  const [surveyTitle, setSurveyTitle] = useState("");
  const printRef = useRef();
  const toast = useToast();
  const navigate = useNavigate();
  const { clearSessionData } = useSession();
  const [isDownloading, setIsDownloading] = useState(false);

  const axiosInstance = createAxiosInstance(async (axiosInstance) => {
    logout(t, clearSessionData, navigate, toast, axiosInstance);
  });

  useEffect(() => {
    initSurveyService(t, toast, axiosInstance);

   
    getStatisticChartsADP(toast, id).then((res) => {
      if (res && res.message) {
        const { charts, survey_title } = res.message;
        if (charts && survey_title) {
          setCharts(charts);
          setSurveyTitle(survey_title);
        }
      }
    });
  }, []);

  const downloadPDF = async () => {
    if (isDownloading) return; // evita doppio click
  
    setIsDownloading(true); // start loading
  
    try {
      const chartImages = [];
  
      // Extract images with html2canvas
      for (let i = 0; i < charts.length; i++) {
        const chartDivId = `chart-${i}`;
        const chartElement = document.getElementById(chartDivId);
  
        if (chartElement) {
          try {
            const canvas = await html2canvas(chartElement, { scale: 2 });
            const imgUrl = canvas.toDataURL('image/jpeg');
  
            chartImages.push({
              title: charts[i].title,
              imgUrl,
            });
          } catch (error) {
            console.error(`Error generating image for ${chartDivId}:`, error);
          }
        }
      }
  
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "-9999px";
      document.body.appendChild(iframe);
      
      // Usa srcdoc per inserire contenuto iniziale
      iframe.srcdoc = `
        <!DOCTYPE html>
        <html>
          <head><title>PDF</title></head>
          <body style='margin:0;padding:0;'></body>
        </html>
      `;
      
      await new Promise((resolve) => {
        iframe.onload = resolve;
      });
      
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const tempContainer = doc.body;

    

      

      tempContainer.style.width = "800px";
  
      const imageLoadPromises = [];
  
      const chartsPerPage = 2;

      for (let i = 0; i < chartImages.length; i += chartsPerPage) {
        const pageDiv = document.createElement("div");
        pageDiv.style.display = "flex";
        pageDiv.style.flexDirection = "column";
        pageDiv.style.alignItems = "center";
        pageDiv.style.justifyContent = "space-evenly";
        pageDiv.style.height = "1122px";
        pageDiv.style.padding = "20px";
      
        if (i === 0) {
          const surveyTitleEl = document.createElement("h1");
          surveyTitleEl.textContent = `Survey Title: ${surveyTitle}`;
          surveyTitleEl.style.textAlign = "center";
          surveyTitleEl.style.fontSize = "28px";
          surveyTitleEl.style.marginBottom = "20px";
          pageDiv.appendChild(surveyTitleEl);
        }
      
        for (let j = i; j < i + chartsPerPage && j < chartImages.length; j++) {
          const { title, imgUrl } = chartImages[j];
      
          const titleEl = document.createElement("h2");
          titleEl.textContent = title;
          titleEl.style.textAlign = "center";
          titleEl.style.margin = "10px 0";
      
          const imgEl = document.createElement("img");
          imgEl.src = imgUrl;
          imgEl.style.maxWidth = "90%";
          imgEl.style.maxHeight = "45%";
          imgEl.style.objectFit = "contain";
          imgEl.style.border = "2px solid black";
          imgEl.style.borderRadius = "8px";

      
          const imgLoaded = new Promise((resolve) => {
            imgEl.onload = resolve;
            imgEl.onerror = resolve;
          });
          imageLoadPromises.push(imgLoaded);
      
          pageDiv.appendChild(titleEl);
          pageDiv.appendChild(imgEl);
        }
      
        tempContainer.appendChild(pageDiv);
      }
      
  
    
  
      await Promise.all(imageLoadPromises);
  
      const opt = {
        margin: 0,
        filename: `${surveyTitle}.pdf`,
        image: { type: "jpeg", quality: 1.5 },
        html2canvas: { scale: 3 },
        jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
      };
  
      await html2pdf().set(opt).from(tempContainer).save();
  
      document.body.removeChild(iframe);
    
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Error",
        description: "There was an error generating the PDF.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false); // stop loading
    }
  };
  

  const renderChart = (chart, index) => {
    const commonLayout = {
      autosize: false,
      width: 900,
      height: 450,
      margin: { t: 30, b: 30, l: 100, r: 50 },
      plot_bgcolor: "#f5f5f5",
    };

    const { type } = chart;

    switch (type) {
      case "ranking": {
        const labels = chart.options[0].data.map((_, i) => `${i + 1}° Posizione`);
        const data = chart.options.map((opt, i) => ({
          type: 'bar',
          name: opt.optionName,
          x: labels,
          y: opt.data,
          marker: { color: `hsl(${(i * 60) % 360}, 70%, 60%)` },
        }));

        return (
          <Plot
            key={index}
            divId={`chart-${index}`}
            data={data}
            layout={{
              ...commonLayout,
              title: chart.title,
              barmode: 'group',
              xaxis: { title: 'Posizione' },
              yaxis: { title: 'Conteggio' },
            }}
          />
        );
      }
      case "rating": {
        return (
          <Plot
            divId={`chart-${index}`}
            key={index}
            data={[{
              type: 'bar',
              x: chart.labels.map(label => `${label} Star`),
              y: chart.data,
              marker: { color: '#3182ce' },
            }]}
            layout={{
              ...commonLayout,
              xaxis: { title: 'Rating' },
              yaxis: { title: 'Percentage', range: [0, 100] },
            }}
          />
        );
      }
      case "boolean": {
        return (
          <Plot
            divId={`chart-${index}`}
            data={[{
              type: "bar",
              x: [3, 1],
              y: ['Yes', 'No'],
              orientation: "h",
              marker: { color: ['#28a745', '#dc3545'] },
              text: ['Yes', 'No'],
              textposition: 'inside',
              hoverinfo: 'x+y',
            }]}
            layout={{
              ...commonLayout,
              title: 'Risposte Yes vs No',
              showlegend: false,
              xaxis: { title: 'Numero di Risposte' },
              yaxis: { title: 'Risposte' },
            }}
          />
        );
      }
      case "dropdown": {
        return (
          <Plot
            divId={`chart-${index}`}
            key={index}
            data={[{
              type: 'bar',
              x: chart.labels,
              y: chart.data,
              marker: { color: 'rgba(54, 162, 235, 0.2)' },
            }]}
            layout={{
              ...commonLayout,
              xaxis: { title: 'Options' },
              yaxis: {
                title: 'Responses',
                range: [0, Math.max(...chart.data) + 1],
              },
              showlegend: false,
              hovermode: 'closest',
            }}
          />
        );
      }
      case "radiogroup":
      case "checkbox": {
        return (
          <Plot
            divId={`chart-${index}`}
            key={index}
            data={[{
              type: 'pie',
              labels: chart.labels,
              values: chart.data,
              marker: { colors: ['#FF6384', '#36A2EB', '#FFCE56'] },
            }]}
            layout={{
              ...commonLayout,
              title: chart.title,
            }}
          />
        );
      }
      case "matrix": {
        return (
          <Plot
            divId={`chart-${index}`}
            key={index}
            data={[{
              type: 'heatmap',
              z: chart.data,
              x: chart.labelsX,
              y: chart.labelsY,
              colorscale: 'teal',
              colorbar: {
                title: 'Value',
                thickness: 15,
              },
              text: chart.data.map(row => row.map(val => String(val))),
              texttemplate: "%{text}",
              textfont: {
                family: 'Arial',
                size: 14,
                color: 'black',
              },
              hoverinfo: 'x+y+z',
            }]}
            layout={{
              ...commonLayout,
              title: chart.title,
              xaxis: { title: 'X Labels' },
              yaxis: { title: 'Y Labels' },
              margin: { t: 50, b: 50, l: 150, r: 60 },
              width: 1000,
              height: 500,
            }}
          />
        );
      }
      default:
        return (
          <Text key={index} fontStyle="italic">
            {t("No chart available for type")}: {type}
          </Text>
        );
    }
  };

  return (
    <Box position="relative" height="100vh" overflow="hidden">
      {/* Fixed Header */}
      <Box
        position="sticky"
        top={0}
        zIndex={10}
        bg="white"
        p={4}
        borderBottomWidth="1px"
        boxShadow="sm"
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Button p={2} colorScheme="red" onClick={() => navigate("/surveys?hideHeader=true&hideNavbar=true")}>
            <MdArrowBack size={20} />
            <Text mx={2}>
              {t("survey.surveyResponseStatisticPage.back")}
            </Text>
          </Button>
          
          <Heading size="lg" textAlign="center" flex="1" px={4} color={"rgba(11,78,90,255)"}>
            {t("survey.surveyResponseStatisticPage.surveyTitle")}: {surveyTitle}
          </Heading>
          
          <Button
  colorScheme="teal"
  onClick={downloadPDF}
  isLoading={isDownloading}
  
>
  {t("survey.surveyResponseStatisticPage.downloadPDF")}
</Button>
        </Flex>
      </Box>

      {/* Scrollable Content */}
      <Box
        ref={printRef}
        p={4}
        overflowY="auto"
        height="calc(100vh - 80px)"
      >
        <VStack spacing={6} w="full">
          {charts.map((chart, index) => (
            <Box
              key={index}
              w="100%"
              maxW="1000px"
              mx="auto"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              mb={10}
              p={4}
              borderWidth="1px"
              borderColor="black"
              borderRadius="md"
              boxShadow="md"
              bg="white"
            >
              <Heading size="md" textAlign="center" mb={4}>
                {chart.title}
              </Heading>
              {renderChart(chart, index)}
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default ChartResponseView;